import * as React from "react";
import { X } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/presentation/components/ui/button";

const NON_CLEARABLE_INPUT_TYPES = new Set([
  "button",
  "checkbox",
  "color",
  "date",
  "datetime-local",
  "file",
  "hidden",
  "image",
  "month",
  "radio",
  "range",
  "reset",
  "submit",
  "time",
  "week",
]);

export interface InputClearButtonProps {
  readonly onClick?: React.MouseEventHandler<HTMLButtonElement>;
  readonly onPointerDown?: React.PointerEventHandler<HTMLButtonElement>;
  readonly disabled?: boolean;
  readonly className?: string;
  readonly label?: string;
}

function InputClearButton({
  onClick,
  onPointerDown,
  disabled,
  className,
  label = "Clear",
}: Readonly<InputClearButtonProps>) {
  return (
    <Button
      type="button"
      variant="ghost"
      size="icon"
      disabled={disabled}
      className={cn("h-7 w-7 shrink-0 text-muted-foreground hover:text-foreground", className)}
      onPointerDown={(event) => {
        event.preventDefault();
        event.stopPropagation();
        onPointerDown?.(event);
      }}
      onClick={(event) => {
        event.preventDefault();
        event.stopPropagation();
        onClick?.(event);
      }}
      aria-label={label}
    >
      <X className="h-3.5 w-3.5" />
    </Button>
  );
}

export interface InputProps extends React.ComponentProps<"input"> {
  readonly showClear?: boolean;
  readonly onClear?: () => void;
  readonly clearLabel?: string;
}

function isClearableInputType(type: React.HTMLInputTypeAttribute | undefined): boolean {
  return !NON_CLEARABLE_INPUT_TYPES.has(type ?? "text");
}

function assignRef<T>(ref: React.Ref<T> | undefined, value: T | null) {
  if (typeof ref === "function") {
    ref(value);
    return;
  }
  if (ref) {
    ref.current = value;
  }
}

const Input = React.forwardRef<HTMLInputElement, Readonly<InputProps>>(
  (
    {
      className,
      type,
      showClear = true,
      onClear,
      clearLabel = "Clear",
      disabled,
      value,
      defaultValue,
      onChange,
      ...props
    },
    ref,
  ) => {
    const inputRef = React.useRef<HTMLInputElement>(null);
    const isControlled = value !== undefined;
    const [uncontrolledValue, setUncontrolledValue] = React.useState(
      () => String(defaultValue ?? ""),
    );
    const currentValue = isControlled ? String(value ?? "") : uncontrolledValue;
    const canClear =
      showClear && isClearableInputType(type) && !disabled && currentValue.length > 0;

    const setRefs = (node: HTMLInputElement | null) => {
      inputRef.current = node;
      assignRef(ref, node);
    };

    const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
      if (!isControlled) {
        setUncontrolledValue(event.target.value);
      }
      onChange?.(event);
    };

    const handleClear = (event: React.MouseEvent<HTMLButtonElement>) => {
      event.preventDefault();
      event.stopPropagation();

      const node = inputRef.current;
      if (node) {
        node.value = "";
      }
      if (!isControlled) {
        setUncontrolledValue("");
      }

      onChange?.({
        target: { value: "" },
        currentTarget: { value: "" },
      } as React.ChangeEvent<HTMLInputElement>);
      onClear?.();
    };

    return (
      <div className="relative w-full min-w-0">
        <input
          type={type}
          className={cn(
            "flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-base shadow-sm transition-colors file:border-0 file:bg-transparent file:text-sm file:font-medium file:text-foreground placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50 md:text-sm",
            canClear && "pr-8",
            className,
          )}
          ref={setRefs}
          disabled={disabled}
          {...props}
          value={value}
          defaultValue={defaultValue}
          onChange={handleChange}
        />
        {canClear ? (
          <InputClearButton
            className="absolute right-0.5 top-1/2 -translate-y-1/2"
            onClick={handleClear}
            label={clearLabel}
          />
        ) : null}
      </div>
    );
  },
);
Input.displayName = "Input";

export { Input, InputClearButton };
